<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
    <v-card-title>Step 5: Map Prior Payments</v-card-title>
    <v-card-text>
      <div class="text-body-2 text-grey-darken-1 mb-4">
        The following payments have been made on this matter. Payments are
        <strong>included</strong> by default. Uncheck any payment that should
        <strong>not</strong> appear on the settlement statement. A payment
        another matter's settlement already counts is shown greyed out and
        cannot be selected here.
      </div>

      <!-- Loading state -->
      <v-skeleton-loader
        v-if="settlementStore.payments.loading"
        type="table-row@4"
        class="mb-4"
      />

      <!-- Payment table -->
      <template v-else-if="settlementStore.displayPayments.length > 0">
        <v-table density="compact" class="mb-2">
          <thead>
            <tr>
              <th class="text-uppercase text-caption">Date</th>
              <th class="text-uppercase text-caption">Description</th>
              <th v-if="showMattersColumn" class="text-uppercase text-caption">
                {{ matterTerm }}s
              </th>
              <th class="text-uppercase text-caption text-right">Amount</th>
              <th class="text-uppercase text-caption text-center w-[80px]">
                Include?
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="payment in paymentRows"
              :key="payment.externalPaymentId"
              :class="{ 'text-disabled': payment.isLocked }"
            >
              <td class="text-body-2">
                {{ getShortDateString(payment.paymentDate) }}
              </td>
              <td class="text-body-2">
                Payment<template
                  v-if="payment.invoices && payment.invoices.length"
                >
                  &mdash;
                  <template
                    v-for="(inv, i) in payment.invoices"
                    :key="inv.timeSheetId"
                  >
                    <template v-if="i > 0">, </template>
                    <a
                      :href="getInvoicePdfUrl(inv.timeSheetId)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-decoration-underline text-primary"
                      >Inv #{{ inv.invoiceNumber || inv.timeSheetId }}</a
                    >
                  </template>
                </template>
              </td>
              <td
                v-if="showMattersColumn"
                class="text-body-2"
                data-cy="payment-matters"
              >
                <template v-if="payment.matterSummary">
                  {{ payment.matterSummary.visible.join(', ') }}
                  <LlTooltip
                    v-if="payment.matterSummary.hiddenCount > 0"
                    :text="payment.matterSummary.all.join(', ')"
                    location="top"
                    max-width="260"
                  >
                    <template #activator>
                      <span
                        class="ml-1 cursor-pointer text-grey-darken-1 text-decoration-underline"
                        tabindex="0"
                        :aria-label="`${matterTerm}s: ${payment.matterSummary.all.join(', ')}`"
                        data-cy="payment-matters-overflow"
                      >
                        +{{ payment.matterSummary.hiddenCount }} more
                      </span>
                    </template>
                  </LlTooltip>
                </template>
              </td>
              <td class="text-body-2 text-right">
                {{ formatCurrency(payment.amount) }}
              </td>
              <td class="text-center">
                <LlTooltip
                  location="top"
                  :max-width="300"
                  :text="lockedHoverText(payment)"
                  :data-cy="`payment-lock-${payment.externalPaymentId}`"
                >
                  <template #activator>
                    <LlCheckbox
                      :model-value="payment.isIncluded"
                      :disabled="payment.isLocked"
                      :aria-label="`Include payment of ${formatCurrency(payment.amount)} on ${getShortDateString(payment.paymentDate)}`"
                      :data-cy="`payment-include-${payment.externalPaymentId}`"
                      class="d-inline-flex"
                      @update:model-value="
                        settlementStore.togglePayment(payment.externalPaymentId)
                      "
                    />
                  </template>
                </LlTooltip>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="bg-grey-lighten-4">
              <td
                :colspan="showMattersColumn ? 3 : 2"
                class="text-body-2 font-weight-bold text-grey-darken-3 pa-3"
              >
                Total Client Payments (included):
              </td>
              <td
                class="text-body-2 font-weight-bold text-grey-darken-3 text-right pa-3"
              >
                {{ formatCurrency(settlementStore.clientPaidTotal) }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </v-table>
      </template>

      <!-- Empty state -->
      <AlertBar v-else type="info" density="default" class="mb-4">
        No non-trust payments found for this matter.
      </AlertBar>

      <!-- Tip box -->
      <AlertBar
        v-if="settlementStore.displayPayments.length > 0"
        type="info"
        class="mb-4"
      >
        <span class="text-body-2">
          <strong>Tip:</strong> Payments start checked (included) unless
          another matter's settlement already counts them. If you see a payment
          that doesn't belong on the statement — for example a post-settlement
          trust transfer — simply uncheck it.
        </span>
      </AlertBar>

      <div class="sett-step-footer">
        <LlSecondaryButton @click="$emit('go-back')">
          &larr; Back
        </LlSecondaryButton>
        <LlSecondaryButton
          color="primary"
          :loading="settlementStore.saving"
          @click="confirmPaymentMapping"
        >
          Confirm Payment Mapping &rarr;
        </LlSecondaryButton>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import AlertBar from '@/components/AlertBar.vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { summarizePaymentMatters } from '@/modules/matters/settlement/utils/paymentMatterNames';
import { formatCurrency, getShortDateString } from '@/services/utils';
import type {
  DisplayPaymentItem,
  MatterNameSummary,
} from '@/models/settlementTypes';

const props = defineProps({
  domain: { type: String, default: '' },
  /** `settlementClientAccounting` flipper — gates the "Matters:" column. */
  clientAccountingEnabled: { type: Boolean, default: false },
});

const emit = defineEmits(['go-back', 'step-completed']);

const settlementStore = useSettlementStore();
const { terms } = storeToRefs(useMeStore());
const matterTerm = computed(() => terms.value?.Matter || 'Matter');

interface PaymentRow extends DisplayPaymentItem {
  matterSummary: MatterNameSummary | null;
}

const paymentRows = computed<PaymentRow[]>(() =>
  settlementStore.displayPayments.map((payment) => ({
    ...payment,
    matterSummary: summarizePaymentMatters(payment.invoices),
  })),
);

/** No row spans multiple matters — an empty column would be pure clutter. */
const showMattersColumn = computed<boolean>(
  () =>
    props.clientAccountingEnabled &&
    paymentRows.value.some((row) => row.matterSummary),
);

// Load fresh payment data when this step mounts (v-if on parent controls lifecycle)
onMounted(async () => {
  if (!settlementStore.settlementId) return;
  await settlementStore.loadPayments();
});

async function confirmPaymentMapping() {
  try {
    await settlementStore.confirmClientPayments();
    emit('step-completed');
  } catch {
    // Error handled by store
  }
}

/** DD-V4-10 hover, mirroring DD-V4-7's claimed-deposit copy. Undefined when the row is
 *  selectable — LlTooltip then renders the bare checkbox with no tooltip wrapper. */
function lockedHoverText(payment: DisplayPaymentItem): string | undefined {
  if (!payment.isLocked) return undefined;
  const matterName = payment.lockedByMatterName?.trim() || 'another matter';
  return (
    `You cannot select this payment because it is associated with a settlement for matter ${matterName}. `
    + 'If this is in error, please edit that settlement to exclude this payment.'
  );
}

/** Build the invoice PDF URL for a given timeSheetId. */
function getInvoicePdfUrl(timeSheetId: string): string {
  return `${props.domain}/v1/invoices/${timeSheetId}/pdf`;
}
</script>
